/**
 * Evidence Vault — THE most important feature of the Trust module.
 *
 * This is how ArgusAI scales to 10+ frameworks. Evidence is collected ONCE
 * and mapped to MANY frameworks. Vanta, Drata, Secureframe all built fortunes
 * on this single primitive.
 *
 * Killer differentiator: `argusAiPrimitive` evidence is auto-collected from
 * inside ArgusAI's own systems (RLS middleware, faithfulness verifier,
 * classification gate, audit hash-chain, etc.). Purple "Auto" badges mark it.
 *
 * Features:
 * - Stats bar: Total / Fresh / Stale / Auto-collected
 * - Filters: search, type, source, framework, status
 * - Sortable table (DataTable grid pattern via useTableSort + SortableHeader)
 * - Right-slide Sheet with full evidence detail + chain of custody timeline
 * - Cross-framework badge with hover popover
 * - Expiration color-coding (red / amber)
 */

import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  FolderArchive, Search, Plus, Upload, Download, Eye, MoreHorizontal,
  FileText, Clock, AlertCircle, CheckCircle2, Sparkles,
  Shield, Settings, BookOpen, GraduationCap, Building2, FlaskConical,
  TrendingUp, UserCheck, BadgeCheck, Siren, Bot, CheckSquare,
  Camera, Database, HardDriveUpload, CloudDownload, Tag, User, Calendar,
  History, ExternalLink, Edit, Trash2, ShieldCheck, Layers, FileDigit,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { SortableHeader, useTableSort } from '@/components/ui/data-table';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  type Evidence, type EvidenceType, type EvidenceSource,
  type FrameworkId,
} from '@/lib/mock-trust-data';
import { useTrustEvidence } from '@/lib/use-trust-evidence';
import { useTrustFrameworks } from '@/lib/use-trust-frameworks';
import { EvidenceExportPackDialog } from '@/components/trust/evidence-export-pack-dialog';
import { downloadEvidence, triggerDownload, generateCsv } from '@/lib/trust-downloads';
import { Package } from 'lucide-react';

export const Route = createFileRoute('/trust/evidence')({
  component: EvidenceVaultPage,
});

// ── Helpers ────────────────────────────────────────────────────────────────

function formatBytes(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

// ── Style Maps ─────────────────────────────────────────────────────────────

const TYPE_STYLES: Record<EvidenceType, { label: string; color: string; icon: typeof FileText }> = {
  configuration: { label: 'Config', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20', icon: Settings },
  log: { label: 'Log', color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20', icon: FileDigit },
  policy: { label: 'Policy', color: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20', icon: BookOpen },
  training: { label: 'Training', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20', icon: GraduationCap },
  vendor: { label: 'Vendor', color: 'bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/20', icon: Building2 },
  test: { label: 'Test', color: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20', icon: FlaskConical },
  risk: { label: 'Risk', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20', icon: TrendingUp },
  personnel: { label: 'Personnel', color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20', icon: UserCheck },
  verification: { label: 'Verification', color: 'bg-lime-500/10 text-lime-600 dark:text-lime-400 border-lime-500/20', icon: BadgeCheck },
  incident: { label: 'Incident', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20', icon: Siren },
  ai: { label: 'AI', color: 'bg-pink-500/10 text-pink-600 dark:text-pink-400 border-pink-500/20', icon: Bot },
  approval: { label: 'Approval', color: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20', icon: CheckSquare },
};

const SOURCE_STYLES: Record<EvidenceSource, { label: string; color: string; icon: typeof HardDriveUpload }> = {
  manual_upload: { label: 'Manual', color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20', icon: HardDriveUpload },
  auto_collected: { label: 'Auto', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20', icon: CloudDownload },
  api_pull: { label: 'API', color: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20', icon: Database },
  screenshot: { label: 'Screenshot', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20', icon: Camera },
  argus_primitive: { label: 'Argus Primitive', color: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20', icon: Sparkles },
};

const STATUS_STYLES: Record<Evidence['status'], { label: string; color: string }> = {
  current: { label: 'Current', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
  expired: { label: 'Expired', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20' },
  expiring_soon: { label: 'Expiring Soon', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' },
  superseded: { label: 'Superseded', color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20' },
};

const FRAMEWORK_BADGE_COLORS: Record<FrameworkId, string> = {
  itar: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
  cmmc_l2: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
  hipaa: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  gdpr: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20',
  soc2: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  iso27001: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20',
  iso42001: 'bg-pink-500/10 text-pink-600 dark:text-pink-400 border-pink-500/20',
  finos_ai_v2: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20',
  nist_ai_rmf: 'bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/20',
  fedramp_moderate: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20',
  pci_dss: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20',
};

// ── Page ───────────────────────────────────────────────────────────────────

function EvidenceVaultPage() {
  // Evidence is auto-collected by ArgusAI primitives. No manual upload.
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | EvidenceType>('all');
  const [sourceFilter, setSourceFilter] = useState<'all' | EvidenceSource>('all');
  const [frameworkFilter, setFrameworkFilter] = useState<'all' | FrameworkId>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | Evidence['status']>('all');
  const [selected, setSelected] = useState<Evidence | null>(null);
  const [exportPackOpen, setExportPackOpen] = useState(false);

  // Real-data: A2 evidence_artifacts → Evidence VM via adapter.
  // Empty array while loading; the existing JSX handles empty list state.
  const { data: allEvidence = [] } = useTrustEvidence();
  const { data: FRAMEWORKS = [] } = useTrustFrameworks();

  // Stats (computed against real data, falls to zeros while loading).
  const stats = useMemo(() => {
    const total = allEvidence.length;
    const fresh = allEvidence.filter(e => e.status === 'current').length;
    const stale = allEvidence.filter(e => e.status === 'expired' || e.status === 'expiring_soon').length;
    const autoCollected = allEvidence.filter(
      e => e.source === 'auto_collected' || e.source === 'argus_primitive' || e.source === 'api_pull',
    ).length;
    const argusPrim = allEvidence.filter(e => !!e.argusAiPrimitive).length;
    return { total, fresh, stale, autoCollected, argusPrim };
  }, [allEvidence]);

  const handleDownload = (ev: Evidence) => {
    downloadEvidence(ev);
    toast.success('Evidence downloaded', { description: ev.fileName ?? ev.title });
  };


  const handleViewOriginal = (ev: Evidence) => {
    if (ev.argusAiPrimitive) {
      toast.info('Argus-primitive evidence', {
        description: `Auto-generated by ${ev.argusAiPrimitive}. Raw output stored in the audit chain.`,
      });
      return;
    }
    // In production, open a viewer for the file. Here we trigger download as fallback.
    downloadEvidence(ev);
  };

  const handleExportList = () => {
    const csv = generateCsv(
      ['Title', 'Type', 'Source', 'Frameworks', 'Owner', 'Effective', 'Expires', 'Version', 'Status'],
      filtered.map(e => [
        e.title, e.type, e.source,
        Array.from(new Set(e.controlMappings.map(m => m.framework))).join(' | '),
        e.ownerName, e.effectiveDate, e.expirationDate ?? '',
        `v${e.versionNumber}`, e.status,
      ]),
    );
    triggerDownload({
      filename: `evidence-export-${new Date().toISOString().split('T')[0]}.csv`,
      content: csv,
      mimeType: 'text/csv',
    });
    toast.success('Exported', { description: `${filtered.length} evidence records.` });
  };

  // Filter pipeline
  const filtered = useMemo(() => {
    return allEvidence.filter(e => {
      if (typeFilter !== 'all' && e.type !== typeFilter) return false;
      if (sourceFilter !== 'all' && e.source !== sourceFilter) return false;
      if (statusFilter !== 'all' && e.status !== statusFilter) return false;
      if (frameworkFilter !== 'all' && !e.controlMappings.some(m => m.framework === frameworkFilter)) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return e.title.toLowerCase().includes(q)
        || e.description.toLowerCase().includes(q)
        || e.tags.some(t => t.toLowerCase().includes(q))
        || e.ownerName.toLowerCase().includes(q);
    });
  }, [allEvidence, search, typeFilter, sourceFilter, frameworkFilter, statusFilter]);

  const { sorted, sortConfig, handleSort } = useTableSort(filtered, 'uploadedAt', 'desc');

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <FolderArchive className="h-6 w-6 text-primary" /> Evidence Vault
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Centralized evidence store. Collect once, map to many frameworks.
            <span className="ml-1 inline-flex items-center gap-1 text-violet-600 dark:text-violet-400 font-medium">
              <Sparkles className="h-3 w-3" /> {stats.argusPrim} Argus primitives auto-collected
            </span>
          </p>
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => setExportPackOpen(true)}>
          <Package className="h-3.5 w-3.5" /> Export Pack
        </Button>
      </div>

      {/* Auto-collected banner — honest about current state (Trust bug #17). */}
      <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        {stats.total > 0 ? (
          <span>
            Evidence is collected from ArgusAI primitives — hash-chain verifications and
            audit-chain status snapshots are captured into the vault. {stats.fresh} fresh,
            {' '}{stats.stale} stale of {stats.total} total.
          </span>
        ) : (
          <span>
            No evidence collected yet. Click <strong>Collect Evidence Now</strong> to capture a
            hash-chain verification + audit-chain status snapshot. Continuous auto-collection is on
            the roadmap.
          </span>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total Evidence</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{stats.fresh}</p>
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> Fresh
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <p className={cn(
              'text-2xl font-bold',
              stats.stale > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground',
            )}>{stats.stale}</p>
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              <AlertCircle className="h-3 w-3" /> Stale / Expiring
            </p>
          </CardContent>
        </Card>
        <Card className="border-violet-500/20 bg-violet-500/5">
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold text-violet-600 dark:text-violet-400 flex items-center justify-center gap-1.5">
              <Sparkles className="h-4 w-4" /> {stats.autoCollected}
            </p>
            <p className="text-xs text-muted-foreground">Auto-collected</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search title, description, tags, owner..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={typeFilter} onValueChange={v => setTypeFilter(v as typeof typeFilter)}>
          <SelectTrigger className="w-36 h-9 text-xs"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {(Object.keys(TYPE_STYLES) as EvidenceType[]).map(t => (
              <SelectItem key={t} value={t}>{TYPE_STYLES[t].label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={sourceFilter} onValueChange={v => setSourceFilter(v as typeof sourceFilter)}>
          <SelectTrigger className="w-36 h-9 text-xs"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {(Object.keys(SOURCE_STYLES) as EvidenceSource[]).map(s => (
              <SelectItem key={s} value={s}>{SOURCE_STYLES[s].label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={frameworkFilter} onValueChange={v => setFrameworkFilter(v as typeof frameworkFilter)}>
          <SelectTrigger className="w-36 h-9 text-xs"><SelectValue placeholder="Framework" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Frameworks</SelectItem>
            {FRAMEWORKS.map(f => (
              <SelectItem key={f.id} value={f.id}>{f.shortName}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={v => setStatusFilter(v as typeof statusFilter)}>
          <SelectTrigger className="w-36 h-9 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="current">Current</SelectItem>
            <SelectItem value="expiring_soon">Expiring Soon</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="superseded">Superseded</SelectItem>
          </SelectContent>
        </Select>

        <span className="text-xs text-muted-foreground ml-auto">
          {filtered.length} of {allEvidence.length}
        </span>
      </div>

      {/* Export evidence list button - right-aligned utility */}

      {/* Table */}
      {sorted.length > 0 ? (
        <div className="rounded-lg border overflow-x-auto bg-card shadow-sm">
          <div className="min-w-[1400px]">
            <div className="grid grid-cols-[minmax(340px,2.2fr)_100px_130px_180px_140px_120px_130px_80px_60px] gap-3 px-4 py-3 bg-muted/50 text-xs uppercase tracking-wider border-b">
              <SortableHeader label="Evidence" sortKey="title" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Type" sortKey="type" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Source" sortKey="source" currentSort={sortConfig} onSort={handleSort} />
              <span className="text-muted-foreground flex items-center gap-1.5">Frameworks</span>
              <SortableHeader label="Owner" sortKey="ownerName" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Effective" sortKey="effectiveDate" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Expires" sortKey="expirationDate" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Version" sortKey="versionNumber" currentSort={sortConfig} onSort={handleSort} />
              <span className="text-muted-foreground text-right">Actions</span>
            </div>

            <div className="divide-y">
              {sorted.map(ev => (
                <EvidenceRow
                  key={ev.id}
                  ev={ev}
                  onSelect={setSelected}
                  onDownload={handleDownload}
                  onViewOriginal={handleViewOriginal}
                  frameworks={FRAMEWORKS}
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <FolderArchive className="h-10 w-10 text-muted-foreground/20 mb-3" />
          <p className="text-sm font-medium">No evidence matches your filters</p>
          <p className="text-xs text-muted-foreground mt-1">
            {search || typeFilter !== 'all' || sourceFilter !== 'all' || frameworkFilter !== 'all' || statusFilter !== 'all'
              ? 'Try adjusting your filters or clearing search'
              : 'ArgusAI primitives will automatically generate evidence as the platform runs'}
          </p>
        </div>
      )}

      {/* Detail Sheet */}
      <EvidenceDetailSheet
        evidence={selected}
        onClose={() => setSelected(null)}
        onDownload={handleDownload}
        onViewOriginal={handleViewOriginal}
        frameworks={FRAMEWORKS}
      />

      {/* Evidence Export Pack dialog */}
      <EvidenceExportPackDialog
        open={exportPackOpen}
        onOpenChange={setExportPackOpen}
      />
    </div>
  );
}

// ── Evidence Row ───────────────────────────────────────────────────────────

function EvidenceRow({
  ev, onSelect, onDownload, onViewOriginal, frameworks,
}: {
  ev: Evidence;
  onSelect: (e: Evidence) => void;
  onDownload: (e: Evidence) => void;
  onViewOriginal: (e: Evidence) => void;
  frameworks: import('@/lib/mock-trust-data').Framework[];
}) {
  const typeStyle = TYPE_STYLES[ev.type];
  const sourceStyle = SOURCE_STYLES[ev.source];
  const TypeIcon = typeStyle.icon;
  const SourceIcon = sourceStyle.icon;
  const daysToExpiry = daysUntil(ev.expirationDate);
  const isArgus = !!ev.argusAiPrimitive;

  // Count unique frameworks this evidence is mapped to
  const frameworkSet = new Set(ev.controlMappings.map(m => m.framework));
  const frameworkCount = frameworkSet.size;
  const topFrameworks = Array.from(frameworkSet).slice(0, 3);

  return (
    <div
      className={cn(
        'grid grid-cols-[minmax(340px,2.2fr)_100px_130px_180px_140px_120px_130px_80px_60px] gap-3 px-4 py-3 items-center hover:bg-muted/30 transition-colors cursor-pointer group',
        isArgus && 'bg-violet-500/[0.02] hover:bg-violet-500/[0.05]',
        ev.status === 'expired' && 'opacity-80',
      )}
      onClick={() => onSelect(ev)}
    >
      {/* Title + description + tags */}
      <div className="flex items-start gap-3 min-w-0">
        <div className={cn(
          'mt-0.5 flex-shrink-0 rounded-md p-1.5',
          isArgus
            ? 'bg-violet-500/10 text-violet-600 dark:text-violet-400'
            : 'bg-muted text-muted-foreground',
        )}>
          {isArgus ? <Sparkles className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-sm font-semibold truncate">{ev.title}</p>
            {isArgus && (
              <Badge variant="outline" className="text-[9px] h-4 px-1 bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/30 gap-0.5">
                <Sparkles className="h-2.5 w-2.5" /> AUTO
              </Badge>
            )}
            <Badge
              variant="outline"
              className={cn('text-[9px] h-4 px-1', STATUS_STYLES[ev.status].color)}
            >
              {STATUS_STYLES[ev.status].label}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground truncate mt-0.5">{ev.description}</p>
          {ev.tags.length > 0 && (
            <div className="flex items-center gap-1 mt-1 flex-wrap">
              {ev.tags.slice(0, 3).map(tag => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground bg-muted/70 px-1.5 py-0.5 rounded"
                >
                  <Tag className="h-2.5 w-2.5" /> {tag}
                </span>
              ))}
              {ev.tags.length > 3 && (
                <span className="text-[10px] text-muted-foreground">+{ev.tags.length - 3}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Type */}
      <Badge variant="outline" className={cn('text-[10px] w-fit gap-1', typeStyle.color)}>
        <TypeIcon className="h-3 w-3" /> {typeStyle.label}
      </Badge>

      {/* Source */}
      <Badge variant="outline" className={cn('text-[10px] w-fit gap-1', sourceStyle.color)}>
        <SourceIcon className="h-3 w-3" /> {sourceStyle.label}
      </Badge>

      {/* Framework mappings */}
      <div className="flex items-center gap-1 flex-wrap" onClick={e => e.stopPropagation()}>
        {topFrameworks.map(fid => {
          const fw = frameworks.find(f => f.id === fid);
          if (!fw) return null;
          return (
            <Badge
              key={fid}
              variant="outline"
              className={cn('text-[9px] h-4 px-1', FRAMEWORK_BADGE_COLORS[fid])}
            >
              {fw.shortName}
            </Badge>
          );
        })}
        {frameworkCount > 3 && (
          <Popover>
            <PopoverTrigger asChild>
              <Badge
                variant="outline"
                className="text-[9px] h-4 px-1 bg-primary/10 text-primary border-primary/20 cursor-pointer hover:bg-primary/20"
              >
                +{frameworkCount - 3}
              </Badge>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-3" align="start">
              <p className="text-xs font-semibold mb-2 flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5" /> Mapped to {frameworkCount} frameworks
              </p>
              <div className="space-y-1.5">
                {Array.from(frameworkSet).map(fid => {
                  const fw = frameworks.find(f => f.id === fid);
                  if (!fw) return null;
                  const mappings = ev.controlMappings.filter(m => m.framework === fid);
                  return (
                    <div key={fid} className="flex items-start justify-between gap-2 text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className={cn('inline-block h-2 w-2 rounded-full', FRAMEWORK_BADGE_COLORS[fid].split(' ')[0].replace('/10', ''))} />
                        <span className="font-medium">{fw.shortName}</span>
                      </div>
                      <span className="text-[10px] text-muted-foreground">
                        {mappings.length} control{mappings.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Owner */}
      <div className="flex items-center gap-1.5 min-w-0">
        <User className="h-3 w-3 text-muted-foreground flex-shrink-0" />
        <span className="text-xs truncate">{ev.ownerName}</span>
      </div>

      {/* Effective date */}
      <span className="text-xs text-muted-foreground">{formatDate(ev.effectiveDate)}</span>

      {/* Expiration date — color-coded */}
      <span
        className={cn(
          'text-xs',
          daysToExpiry !== null && daysToExpiry < 0 && 'text-red-600 dark:text-red-400 font-semibold',
          daysToExpiry !== null && daysToExpiry >= 0 && daysToExpiry <= 30 && 'text-amber-600 dark:text-amber-400 font-semibold',
          (daysToExpiry === null || daysToExpiry > 30) && 'text-muted-foreground',
        )}
        title={ev.expirationDate ? `${daysToExpiry} days` : 'No expiration'}
      >
        {ev.expirationDate ? (
          <span className="flex items-center gap-1">
            {daysToExpiry !== null && daysToExpiry < 0 && <AlertCircle className="h-3 w-3" />}
            {daysToExpiry !== null && daysToExpiry >= 0 && daysToExpiry <= 30 && <Clock className="h-3 w-3" />}
            {formatDate(ev.expirationDate)}
          </span>
        ) : (
          <span className="text-muted-foreground/60">—</span>
        )}
      </span>

      {/* Version */}
      <span className="text-xs text-muted-foreground font-mono">v{ev.versionNumber}</span>

      {/* Actions */}
      <div className="text-right" onClick={e => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Actions">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => onSelect(ev)}>
              <Eye className="h-3.5 w-3.5 mr-2" /> View Details
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onDownload(ev)}>
              <Download className="h-3.5 w-3.5 mr-2" /> Download
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onViewOriginal(ev)}>
              <ExternalLink className="h-3.5 w-3.5 mr-2" /> View Original
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

// ── Detail Sheet ───────────────────────────────────────────────────────────

function EvidenceDetailSheet({
  evidence, onClose, onDownload, onViewOriginal, frameworks,
}: {
  evidence: Evidence | null;
  onClose: () => void;
  onDownload: (ev: Evidence) => void;
  onViewOriginal: (ev: Evidence) => void;
  frameworks: import('@/lib/mock-trust-data').Framework[];
}) {
  const [auditorVisible, setAuditorVisible] = useState(evidence?.auditorVisible ?? false);

  // Keep switch in sync when a new evidence is selected
  useMemo(() => {
    if (evidence) setAuditorVisible(evidence.auditorVisible);
  }, [evidence]);

  if (!evidence) return null;

  const typeStyle = TYPE_STYLES[evidence.type];
  const sourceStyle = SOURCE_STYLES[evidence.source];
  const TypeIcon = typeStyle.icon;
  const SourceIcon = sourceStyle.icon;
  const isArgus = !!evidence.argusAiPrimitive;
  const daysToExpiry = daysUntil(evidence.expirationDate);

  // Group control mappings by framework
  const mappingsByFramework = evidence.controlMappings.reduce((acc, m) => {
    if (!acc[m.framework]) acc[m.framework] = [];
    acc[m.framework].push(m);
    return acc;
  }, {} as Record<FrameworkId, typeof evidence.controlMappings>);

  return (
    <Sheet open={!!evidence} onOpenChange={open => { if (!open) onClose(); }}>
      <SheetContent className="w-[640px] sm:max-w-[640px] p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className={cn(
          'px-6 py-4 border-b',
          isArgus && 'bg-violet-500/5',
        )}>
          <div className="flex items-start gap-3">
            <div className={cn(
              'rounded-lg p-2 flex-shrink-0',
              isArgus
                ? 'bg-violet-500/10 text-violet-600 dark:text-violet-400'
                : 'bg-muted text-muted-foreground',
            )}>
              {isArgus ? <Sparkles className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap mb-1">
                <Badge variant="outline" className={cn('text-[10px] gap-1', typeStyle.color)}>
                  <TypeIcon className="h-3 w-3" /> {typeStyle.label}
                </Badge>
                <Badge variant="outline" className={cn('text-[10px] gap-1', sourceStyle.color)}>
                  <SourceIcon className="h-3 w-3" /> {sourceStyle.label}
                </Badge>
                <Badge variant="outline" className={cn('text-[10px]', STATUS_STYLES[evidence.status].color)}>
                  {STATUS_STYLES[evidence.status].label}
                </Badge>
                {isArgus && (
                  <Badge variant="outline" className="text-[10px] bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/30 gap-0.5">
                    <Sparkles className="h-2.5 w-2.5" /> ARGUS PRIMITIVE
                  </Badge>
                )}
              </div>
              <SheetTitle className="text-base leading-tight">{evidence.title}</SheetTitle>
              <SheetDescription className="text-xs mt-1 leading-relaxed">
                {evidence.description}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="px-6 py-5 space-y-6">

            {/* Argus primitive call-out */}
            {isArgus && (
              <div className="rounded-lg border border-violet-500/20 bg-violet-500/5 p-3">
                <div className="flex items-start gap-2">
                  <Sparkles className="h-4 w-4 text-violet-600 dark:text-violet-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-violet-700 dark:text-violet-300">
                      Auto-collected from ArgusAI system
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      This evidence is continuously generated by the{' '}
                      <code className="font-mono text-[11px] bg-violet-500/10 px-1 py-0.5 rounded">
                        {evidence.argusAiPrimitive}
                      </code>{' '}
                      primitive. No manual upload required. Chain of custody is tamper-evident.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* File Metadata */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" /> File Metadata
              </h3>
              <div className="rounded-lg border bg-muted/30 divide-y text-xs">
                <Row label="Filename" value={evidence.fileName ?? '—'} mono />
                <Row label="Size" value={formatBytes(evidence.fileSize)} />
                <Row label="MIME Type" value={evidence.mimeType ?? '—'} mono />
                <Row label="Version" value={`v${evidence.versionNumber}`} mono />
                {evidence.previousVersionId && (
                  <Row label="Previous Version" value={evidence.previousVersionId} mono />
                )}
                <Row label="Redacted Version" value={evidence.redactedVersion ? 'Yes' : 'No'} />
              </div>
            </section>

            {/* Control Mappings */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" />
                Framework Mappings
                <Badge variant="outline" className="text-[9px] ml-auto h-4 px-1.5">
                  {Object.keys(mappingsByFramework).length} frameworks · {evidence.controlMappings.length} controls
                </Badge>
              </h3>
              <div className="space-y-2">
                {(Object.entries(mappingsByFramework) as [FrameworkId, typeof evidence.controlMappings][]).map(([fid, maps]) => {
                  const fw = frameworks.find(f => f.id === fid);
                  if (!fw) return null;
                  return (
                    <div key={fid} className="rounded-lg border p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={cn('text-[10px]', FRAMEWORK_BADGE_COLORS[fid])}>
                            {fw.shortName}
                          </Badge>
                          <span className="text-xs font-medium">{fw.name}</span>
                        </div>
                        <span className="text-[10px] text-muted-foreground">
                          {maps.length} control{maps.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="space-y-1">
                        {maps.map(m => (
                          <div key={m.controlId} className="flex items-start justify-between gap-2 text-xs py-1 border-t first:border-t-0">
                            <div className="min-w-0 flex-1">
                              <code className="font-mono text-[10px] bg-muted px-1 py-0.5 rounded">{m.controlId}</code>
                              <p className="text-xs text-muted-foreground truncate mt-0.5">{m.controlName}</p>
                            </div>
                            <Badge
                              variant="outline"
                              className={cn(
                                'text-[9px] h-4 px-1 flex-shrink-0',
                                m.relevance === 'primary'
                                  ? 'bg-primary/10 text-primary border-primary/20'
                                  : 'bg-muted text-muted-foreground',
                              )}
                            >
                              {m.relevance}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Dates & Review */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" /> Dates & Review Cycle
              </h3>
              <div className="rounded-lg border bg-muted/30 divide-y text-xs">
                <Row label="Effective Date" value={formatDate(evidence.effectiveDate)} />
                <Row
                  label="Expiration"
                  value={
                    evidence.expirationDate
                      ? `${formatDate(evidence.expirationDate)} · ${daysToExpiry !== null && daysToExpiry < 0 ? `${Math.abs(daysToExpiry)} days overdue` : `${daysToExpiry} days`}`
                      : '—'
                  }
                  valueClass={
                    daysToExpiry !== null && daysToExpiry < 0 ? 'text-red-600 dark:text-red-400 font-semibold' :
                    daysToExpiry !== null && daysToExpiry <= 30 ? 'text-amber-600 dark:text-amber-400 font-semibold' :
                    undefined
                  }
                />
                <Row label="Review Cycle" value={evidence.reviewCycle} />
                <Row label="Next Review" value={formatDate(evidence.nextReviewAt)} />
              </div>
            </section>

            {/* Ownership */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" /> Ownership
              </h3>
              <div className="rounded-lg border bg-muted/30 divide-y text-xs">
                <Row label="Owner" value={evidence.ownerName} />
                <Row label="Uploaded By" value={evidence.uploadedByName} />
                <Row label="Uploaded At" value={formatDateTime(evidence.uploadedAt)} />
              </div>
            </section>

            {/* Tags */}
            {evidence.tags.length > 0 && (
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Tag className="h-3.5 w-3.5" /> Tags
                </h3>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {evidence.tags.map(tag => (
                    <Badge key={tag} variant="outline" className="text-[10px]">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </section>
            )}

            {/* Chain of Custody */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                <History className="h-3.5 w-3.5" /> Chain of Custody
                <Badge variant="outline" className="text-[9px] ml-auto h-4 px-1.5">
                  {evidence.chainOfCustody.length} event{evidence.chainOfCustody.length !== 1 ? 's' : ''}
                </Badge>
              </h3>
              <div className="rounded-lg border bg-muted/30 p-3">
                <div className="relative space-y-3">
                  {evidence.chainOfCustody.map((entry, idx) => {
                    const isLast = idx === evidence.chainOfCustody.length - 1;
                    return (
                      <div key={idx} className="flex items-start gap-3 relative">
                        {!isLast && (
                          <div className="absolute left-[11px] top-6 bottom-[-12px] w-px bg-border" />
                        )}
                        <div className={cn(
                          'h-[22px] w-[22px] rounded-full flex items-center justify-center flex-shrink-0 z-10 border-2',
                          idx === 0
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-card text-muted-foreground border-border',
                        )}>
                          <CheckCircle2 className="h-3 w-3" />
                        </div>
                        <div className="min-w-0 flex-1 pt-0.5">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs font-medium">
                              <span className="capitalize">{entry.action.replace(/_/g, ' ')}</span>
                              {' by '}
                              <span className="text-muted-foreground">{entry.userName}</span>
                            </p>
                            <span className="text-[10px] text-muted-foreground">{timeAgo(entry.timestamp)}</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {formatDateTime(entry.timestamp)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Auditor Visible Toggle */}
            <section>
              <div className="rounded-lg border bg-card p-3 flex items-center justify-between">
                <div className="min-w-0 pr-3">
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    <Eye className="h-4 w-4" /> Auditor Visible
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Allow external auditors to view this evidence during assessments.
                  </p>
                </div>
                <Switch
                  checked={auditorVisible}
                  onCheckedChange={v => {
                    setAuditorVisible(v);
                    toast.success(`Evidence ${v ? 'visible' : 'hidden'} to auditors`);
                  }}
                />
              </div>
            </section>

          </div>
        </ScrollArea>

        {/* Footer Actions */}
        <Separator />
        <div className="px-6 py-3 flex items-center gap-2 bg-muted/20">
          <Button
            size="sm"
            className="gap-1.5"
            onClick={() => onDownload(evidence)}
          >
            <Download className="h-3.5 w-3.5" /> Download
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => onViewOriginal(evidence)}
          >
            <ExternalLink className="h-3.5 w-3.5" /> View Original
          </Button>
          <div className="ml-auto">
            <Button size="sm" variant="ghost" onClick={onClose}>
              <X className="h-3.5 w-3.5 mr-1" /> Close
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Small helpers ──────────────────────────────────────────────────────────

function Row({
  label, value, mono, valueClass,
}: {
  label: string;
  value: string;
  mono?: boolean;
  valueClass?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={cn(
          'text-right truncate max-w-[60%]',
          mono && 'font-mono text-[11px]',
          valueClass,
        )}
        title={value}
      >
        {value}
      </span>
    </div>
  );
}
